import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Boxes,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Compass,
  CreditCard,
  FileText,
  Filter,
  Gauge,
  GitBranch,
  Handshake,
  Headphones,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  Lightbulb,
  LineChart,
  Link2,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  MousePointerClick,
  NotebookPen,
  Package,
  PenLine,
  Percent,
  PieChart,
  Presentation,
  Receipt,
  RefreshCw,
  Repeat,
  Rocket,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Tag,
  Target,
  TrendingUp,
  Truck,
  Users,
  Workflow,
  Wrench,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { GlossTile } from "@/components/panels";
import GuideDialog, { answerKey, connectedSources } from "@/components/playbooks/GuideDialog";
import { useLiveResource } from "@/hooks/useLiveResource";
import { STARTER_GUIDES } from "@/data/starterGuides";
import { GUIDE_PROMPTS } from "@/data/guidePrompts";
import { useChat } from "@/components/chat/ChatWorkspaceContext";

// Every `icon` value used by a guide must resolve here. Unknown keys fall back
// to Lightbulb rather than rendering undefined and crashing the detail modal.
const ICONS = {
  BarChart3,
  BookOpen,
  Boxes,
  CalendarDays,
  ClipboardCheck,
  Compass,
  CreditCard,
  FileText,
  Filter,
  Gauge,
  GitBranch,
  Handshake,
  Headphones,
  Image: ImageIcon,
  Layers,
  LayoutGrid,
  Lightbulb,
  LineChart,
  Link2,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  NotebookPen,
  Package,
  Percent,
  PieChart,
  Presentation,
  Receipt,
  RefreshCw,
  Repeat,
  Rocket,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Tag,
  Target,
  TrendingUp,
  Truck,
  Users,
  Workflow,
  Wrench,
};

export const OUTCOMES = [
  "Revenue",
  "Conversion Rate",
  "Cost Per New Customer",
  "Lifetime Value",
  "Average Order Value",
  "Creative",
  "Operations",
];

export const CORE_GUIDES = [
  {
    id: "lifecycle-flow",
    icon: "GitBranch",
    color: "#4690D9",
    category: "Lifecycle",
    assetTypes: ["Email/SMS Flow"],
    outcomes: ["Lifetime Value", "Revenue"],
    rank: 1,
    title: "Plan a Lifecycle Message Flow",
    labels: ["Journey", "Drafts", "Approval"],
    helps: "Frame a request for a trigger-based customer journey with timing, decision points, draft messages, and an explicit approval boundary before activation.",
    before: [
      { item: "Journey type and entry trigger" },
      { item: "Audience and desired outcome" },
      { item: "Message count and rough cadence" },
      { item: "Offer or incentive", optional: true },
    ],
    outline: [
      "Map the journey, timing, decision points, and exit criteria.",
      "Review the journey logic before any messages are drafted.",
      "Draft each message in the approved brand voice with clear calls to action.",
      "Return all assets for review; do not activate or send without approval.",
    ],
    tip: "Approve the journey logic first. Structural changes are easier before the message set is drafted.",
  },
  {
    id: "landing-review",
    icon: "TrendingUp",
    color: "#16A34A",
    category: "Conversion",
    assetTypes: ["Landing Page"],
    outcomes: ["Conversion Rate"],
    rank: 2,
    title: "Review a Landing Page",
    labels: ["Evidence", "Priorities", "Variant"],
    helps: "Frame a conversion review around an existing page, its traffic, the desired action, and a short list of evidence-backed improvements.",
    before: [
      { item: "Page URL" },
      { item: "Primary traffic source" },
      { item: "Desired visitor action" },
      { item: "Current conversion evidence", optional: true },
    ],
    outline: [
      "Review available traffic and conversion evidence for the page.",
      "Identify the highest-impact friction points across clarity, proof, mobile use, and the action path.",
      "Rank proposed changes by expected impact and effort.",
      "Prepare a reviewable variant plan; do not publish changes without approval.",
    ],
    tip: "Ask for one prioritized change set rather than several simultaneous edits that cannot be measured independently.",
  },
  {
    id: "product-page",
    icon: "Package",
    color: "#EC4899",
    category: "Conversion",
    assetTypes: ["Product Page"],
    outcomes: ["Conversion Rate", "Average Order Value"],
    rank: 3,
    title: "Improve a Product Page",
    labels: ["Objections", "Merchandising", "Review"],
    helps: "Frame a product-page improvement request around the main customer objection, proof, benefit clarity, and a reviewable unpublished change set.",
    before: [
      { item: "Product page URL" },
      { item: "Primary customer objection" },
      { item: "Best available proof or reviews", optional: true },
      { item: "Related products or bundles", optional: true },
    ],
    outline: [
      "Review current page evidence and identify the largest conversion gaps.",
      "Propose a benefit-led structure that addresses the primary objection.",
      "Specify proof, merchandising, and mobile action improvements.",
      "Return a reviewable draft or implementation plan; do not publish without approval.",
    ],
    tip: "Start with the real objection customers raise most often instead of rewriting around product features alone.",
  },
  {
    id: "campaign-page",
    icon: "ShoppingBag",
    color: "#96BF48",
    category: "Campaign",
    assetTypes: ["Landing Page"],
    outcomes: ["Conversion Rate", "Revenue", "Cost Per New Customer"],
    rank: 4,
    title: "Plan a Campaign Landing Page",
    labels: ["Offer", "Structure", "Review"],
    helps: "Frame a focused landing-page request with a clear offer, audience, traffic context, proof plan, and separate review checkpoints for design and build.",
    before: [
      { item: "Product or offer" },
      { item: "Primary benefit or promise" },
      { item: "Target audience" },
      { item: "Examples or proof", optional: true },
    ],
    outline: [
      "Define the page goal, audience, traffic context, and primary action.",
      "Prepare a page structure covering the promise, benefits, proof, and call to action.",
      "Review the proposed structure before implementation begins.",
      "Keep any implementation unpublished until a second approval is recorded.",
    ],
    tip: "Keep structure and implementation as separate review steps so feedback arrives before costly rework.",
  },
  {
    id: "message-campaign",
    icon: "Mail",
    color: "#4690D9",
    category: "Lifecycle",
    assetTypes: ["Email/SMS Campaign"],
    outcomes: ["Revenue", "Lifetime Value"],
    rank: 5,
    title: "Draft a Message Campaign",
    labels: ["Audience", "Copy", "Approval"],
    helps: "Frame a request for a coordinated customer-message campaign with audience, offer, timing, draft copy, and a no-send review boundary.",
    before: [
      { item: "Promotion or announcement" },
      { item: "Audience segment" },
      { item: "Primary offer or angle" },
      { item: "Desired send window", optional: true },
    ],
    outline: [
      "Review prior approved brand examples and the campaign objective.",
      "Draft subject options, message copy, and one clear action for each step.",
      "Organize the sequence and recommended timing for review.",
      "Do not schedule or send any message without explicit approval.",
    ],
    tip: "Include examples of past messages that performed well so the request is grounded in the brand's own evidence.",
  },
  {
    id: "social-batch",
    icon: "Smartphone",
    color: "#7C3AED",
    category: "Creative",
    assetTypes: ["Social Content"],
    outcomes: ["Creative"],
    rank: 6,
    title: "Plan a Social Content Batch",
    labels: ["Concepts", "Calendar", "Review"],
    helps: "Frame a batch-content request with channels, themes, quantity, creative inputs, and an approval-ready calendar rather than an implied publishing action.",
    before: [
      { item: "Target channels" },
      { item: "Topic, product, or promotion" },
      { item: "Number of posts" },
      { item: "Approved source assets", optional: true },
    ],
    outline: [
      "Define the content themes, audience, and objective for the batch.",
      "Draft platform-appropriate concepts and captions in the approved voice.",
      "Organize the batch into a reviewable calendar with asset requirements.",
      "Do not publish or schedule content without approval.",
    ],
    tip: "Ask for a coherent batch with one objective so the final calendar feels connected rather than random.",
  },
  {
    id: "business-review",
    icon: "BarChart3",
    color: "#16A34A",
    category: "Analytics",
    assetTypes: ["Analysis"],
    outcomes: ["Revenue", "Operations"],
    rank: 7,
    title: "Request a Business Performance Review",
    labels: ["Evidence", "Trends", "Actions"],
    helps: "Frame an analysis request around a time period, a specific business question, available evidence, and a short list of prioritized next actions.",
    before: [
      { item: "Time period" },
      { item: "Business question or concern" },
      { item: "Most important metric", optional: true },
    ],
    outline: [
      "Use only available, workspace-approved business evidence.",
      "Explain the important trends and why they may have moved, separating facts from interpretation.",
      "Call out missing or stale sources instead of substituting values.",
      "End with three prioritized recommendations and the evidence behind each one.",
    ],
    tip: "Ask for the reason behind the movement, but require facts and interpretation to stay visibly separate.",
  },
  {
    id: "product-launch",
    icon: "Rocket",
    color: "#D97706",
    category: "Strategy",
    assetTypes: ["Launch Plan"],
    outcomes: ["Revenue", "Cost Per New Customer"],
    rank: 8,
    title: "Plan a Product Launch",
    labels: ["Timeline", "Assets", "Approvals"],
    helps: "Frame a launch request with audience, timing, channels, dependencies, assets, and ordered approval checkpoints before any external action.",
    before: [
      { item: "Product and launch date" },
      { item: "Target audience" },
      { item: "Owned channels" },
      { item: "Launch offer", optional: true },
    ],
    outline: [
      "Start with a week-by-week launch timeline and dependency list.",
      "Define the required page, message, and creative deliverables.",
      "Sequence review checkpoints before implementation, scheduling, or publication.",
      "Return one ordered checklist of decisions and approvals needed from the team.",
    ],
    tip: "Approve the timeline first. It is the shared dependency map for every later launch deliverable.",
  },
];

// The eight original guides keep ranks 1-8 so the recommended order is stable;
// the ecommerce starter set continues from rank 9.
export const GUIDES = [...CORE_GUIDES, ...STARTER_GUIDES];

// Keep store-performance guidance defined for a later release, but do not
// expose it in the simplified client dashboard.
export const AGENT_GUIDES = GUIDES.filter((guide) => guide.id !== "business-review");

const STEPS = [
  { icon: MousePointerClick, label: "Choose a task" },
  { icon: CheckCircle2, label: "See what you’ll get" },
  { icon: PenLine, label: "Add context if you like" },
  { icon: MessageSquare, label: "Try it in Chat" },
];

export const SORT_OPTIONS = [
  { value: "recommended", label: "Recommended" },
  { value: "az", label: "A – Z" },
];

// A card shows this many tags before "+N"; the guide's own popup lists them all.
export const CARD_TAGS = 4;
// The original eight guides hold ranks 1-8 and lead the Recommended order.
export const RECOMMENDED_RANK = 8;
export const isRecommended = (guide) => Number.isFinite(guide?.rank) && guide.rank <= RECOMMENDED_RANK;

export function filterGuides(guides, filters) {
  const search = filters.search?.trim().toLowerCase() ?? "";
  return guides.filter((guide) => {
    const searchable = [guide.title, guide.helps, guide.category, ...guide.labels, ...guide.assetTypes, ...guide.outcomes]
      .join(" ")
      .toLowerCase();
    return (!search || searchable.includes(search))
      && (!filters.assetType || filters.assetType === "all" || guide.assetTypes.includes(filters.assetType))
      && (!filters.outcome || filters.outcome === "all" || guide.outcomes.includes(filters.outcome));
  });
}

export function sortGuides(guides, sort) {
  const sorted = [...guides];
  if (sort === "az") return sorted.sort((left, right) => left.title.localeCompare(right.title));
  return sorted.sort((left, right) => (left.rank ?? 0) - (right.rank ?? 0));
}

export function hasActiveFilters({ search, assetType, outcome, sort }) {
  return Boolean(search?.trim()) || (assetType && assetType !== "all") || (outcome && outcome !== "all") || (sort && sort !== "recommended");
}

// The prompt is written the way the team actually talks to an agent in Slack:
// an @mention, first-person context before the ask, and a stated destination for
// the output. Per-guide copy lives in GUIDE_PROMPTS; anything unmapped falls
// back to a generic construction rather than breaking.
//
// The mention is ALWAYS the literal @AGENT_NAME placeholder. We deliberately do
// not interpolate a real name: no Slack handle exists in any payload, an agent's
// display name is not necessarily its handle, and in demo view it would inject a
// fabricated brand name into text people paste into a real Slack channel. A
// placeholder the user must replace is safer than a plausible wrong mention.
//
// No workspace line either. The agent in a Slack channel already knows which
// brand it works for, so restating it read like form metadata rather than
// something a person would type.
export const AGENT_MENTION_PLACEHOLDER = "@AGENT_NAME";

/**
 * The message the client takes to Slack. `answers` holds what they typed against each context line, keyed by
 * `answerKey`; a line they filled in carries their own words, one they left blank stays a bare heading so the
 * agent asks for it instead of inventing it.
 */
function buildGuidePrompt(guide, notes, answers, mention) {
  const copy = GUIDE_PROMPTS[guide.id];
  const intro = copy?.intro
    ?? `I want your help with this: ${guide.title.toLowerCase()}. ${guide.helps}`;
  const close = copy?.close
    ?? "Bring it back to me as a reviewable draft — do not publish, send or schedule anything without my approval.";
  const trimmedNotes = String(notes ?? "").trim();
  const answered = (item) => String(answers?.[answerKey(item.item)] ?? "").trim();
  const anyAnswer = guide.before.some((item) => answered(item));

  return [
    `${mention}${intro}`,
    "",
    "What I can give you:",
    ...guide.before.map((item) => {
      const value = answered(item);
      if (value) return `- ${item.item}: ${value}`;
      return `- ${item.item}${item.optional ? " (optional)" : ""}`;
    }),
    "",
    trimmedNotes
      ? `Also worth knowing: ${trimmedNotes}`
      : anyAnswer
        ? "Pull the rest from the tools you are connected to, and ask me about anything you still need."
        : "If any of that context is missing on your side, ask me before you start.",
    "",
    close,
  ].join("\n");
}

export function buildSlackPrompt(guide, notes, answers = {}) {
  return buildGuidePrompt(guide, notes, answers, `${AGENT_MENTION_PLACEHOLDER} `);
}

/** A ready-to-review Chat draft, with each guide's evidence and approval boundaries intact. */
export function buildChatPrompt(guide, notes, answers = {}) {
  return buildGuidePrompt(guide, notes, answers, "");
}

// The agent's name and its connections only decorate the guide dialog, so they are read once per visit; the
// pages that own them keep themselves fresh.
const READ_ONCE = { refreshMs: 0 };
const NO_ARGS = [];

export default function Playbooks() {
  const navigate = useNavigate();
  const chat = useChat();
  const controller = chat?.controller;
  const launchRef = useRef(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const guideParam = searchParams.get("guide");
  const [active, setActive] = useState(null);
  const [requestNotes, setRequestNotes] = useState("");
  const [answers, setAnswers] = useState({});
  const [search, setSearch] = useState("");
  const [assetType, setAssetType] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [sort, setSort] = useState("recommended");
  const profileResource = useLiveResource("getAgentProfile", NO_ARGS, READ_ONCE);
  const connectionsResource = useLiveResource("getAgentConnections", NO_ARGS, READ_ONCE);

  useEffect(() => {
    setActive((current) => {
      if (!guideParam) return null;
      if (current?.id === guideParam) return current;
      return AGENT_GUIDES.find((guide) => guide.id === guideParam) ?? null;
    });
  }, [guideParam]);

  useEffect(() => {
    setRequestNotes("");
    setAnswers({});
  }, [guideParam]);

  // Cancel navigation when this guide closes, the page leaves, or the workspace changes.
  // A late creation can never carry a prompt into a different identity/workspace.
  useEffect(() => {
    launchRef.current = null;
    setLaunching(false);
    setLaunchError(null);
    return () => { launchRef.current = null; };
  }, [guideParam, controller?.scopeKey]);

  const Icon = active ? (ICONS[active.icon] ?? Lightbulb) : null;
  const assetTypes = useMemo(
    () => [...new Set(AGENT_GUIDES.flatMap((guide) => guide.assetTypes))].sort(),
    []
  );
  const outcomes = useMemo(
    () => OUTCOMES.filter((item) => AGENT_GUIDES.some((guide) => guide.outcomes.includes(item))),
    []
  );
  const visibleGuides = useMemo(
    () => sortGuides(filterGuides(AGENT_GUIDES, { search, assetType, outcome }), sort),
    [assetType, outcome, search, sort]
  );
  const filtered = hasActiveFilters({ search, assetType, outcome, sort });
  const promptPreview = active ? buildChatPrompt(active, requestNotes, answers) : "";
  const agentName = profileResource.data?.agent?.name || controller?.agent?.name || "Your agent";
  const availabilityMessage = !controller || controller.loading
    ? "Connecting to your agent…"
    : !controller.agent.available ? "Chat will be ready when your agent is connected."
      : !controller.agent.online ? "Your agent is offline. Reconnect to check its status." : "";
  const launchDisabled = Boolean(availabilityMessage) || promptPreview.length > 8000;
  const sources = useMemo(() => connectedSources(connectionsResource.data), [connectionsResource.data]);
  const sourcesStatus = connectionsResource.data ? "ready" : connectionsResource.status === "error" ? "error" : "loading";

  const open = (guide) => {
    const next = new URLSearchParams(searchParams);
    next.set("guide", guide.id);
    setSearchParams(next);
  };

  const close = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("guide");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearch("");
    setAssetType("all");
    setOutcome("all");
    setSort("recommended");
  };

  const copyPrompt = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(promptPreview);
      toast.success("Prompt copied", { description: "Your task and context are ready to use." });
    } catch {
      toast.error("Prompt could not be copied", { description: "Select the prompt below and copy it manually." });
    }
  };

  const tryWithAgent = async () => {
    if (!active || launchRef.current || launchDisabled) return;
    const request = {};
    launchRef.current = request;
    setLaunching(true);
    setLaunchError(null);
    try {
      const id = await controller.createSession({ draft: promptPreview, activate: false });
      if (launchRef.current !== request) return;
      if (!id) {
        setLaunchError("We couldn’t open a new chat. Your task and context are still here. Try again.");
        return;
      }
      controller.setActiveId(id);
      chat.setFullscreen?.(false);
      navigate("/agent/chat");
    } catch {
      if (launchRef.current === request) setLaunchError("We couldn’t open a new chat. Your task and context are still here. Try again.");
    } finally {
      if (launchRef.current === request) {
        launchRef.current = null;
        setLaunching(false);
      }
    }
  };

  return (
    <div className="fade-up" data-testid="playbooks-page">
      <PageHeader
        title="Get Better Results"
        subtitle="Find a useful task, see what you’ll get, and start a fresh conversation with your agent."
        testId="playbooks-header"
      />

      <div data-testid="playbooks-how-it-works" className="gos-card mb-8 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between md:px-7">
        {STEPS.map((step, index) => (
          <div key={step.label} className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sm-accent/15 text-sm-primary">
              <step.icon size={14} />
            </span>
            <div>
              <div className="label-eyebrow text-[9px] text-sm-muted">Step {index + 1}</div>
              <div className="text-sm font-semibold text-sm-ink">{step.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="gos-card mb-4 p-4" data-testid="playbooks-filters">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(12rem,2fr)_repeat(3,minmax(9rem,1fr))]">
          <label className="relative">
            <span className="sr-only">Search guides</span>
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm-ink/35" />
            <input
              data-testid="playbooks-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search guidance"
              className="h-10 w-full rounded-lg border border-sm-ink/10 bg-white pl-9 pr-3 text-sm outline-none focus:border-sm-accent/60"
            />
          </label>
          <select
            data-testid="playbooks-asset-filter"
            aria-label="Asset type"
            value={assetType}
            onChange={(event) => setAssetType(event.target.value)}
            className="h-10 rounded-lg border border-sm-ink/10 bg-white px-3 text-sm text-sm-ink/70"
          >
            <option value="all">All asset types</option>{assetTypes.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select
            data-testid="playbooks-outcome-filter"
            aria-label="Business outcome"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="h-10 rounded-lg border border-sm-ink/10 bg-white px-3 text-sm text-sm-ink/70"
          >
            <option value="all">All outcomes</option>{outcomes.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select
            data-testid="playbooks-sort"
            aria-label="Sort guidance"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="h-10 rounded-lg border border-sm-ink/10 bg-white px-3 text-sm text-sm-ink/70"
          >
            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-sm-ink/[0.07] pt-3">
          <span className="text-xs text-sm-ink/50" data-testid="playbooks-result-count">
            Showing <span className="font-semibold text-sm-ink/75">{visibleGuides.length}</span> of {AGENT_GUIDES.length} guides
          </span>
          {filtered ? (
            <button
              data-testid="playbooks-reset-filters"
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 rounded-full border border-sm-ink/12 px-3 py-1 text-xs font-semibold text-sm-ink/60 transition-colors hover:border-sm-accent/45 hover:text-sm-primary"
            >
              <X size={12} /> Reset filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {visibleGuides.map((guide) => {
          const CardIcon = ICONS[guide.icon] ?? Lightbulb;
          const tags = [
            ...guide.assetTypes.map((label) => ({ label, kind: "asset" })),
            ...guide.outcomes.map((label) => ({ label, kind: "outcome" })),
          ];
          const shownTags = tags.slice(0, CARD_TAGS);
          const steps = Array.isArray(guide.outline) ? guide.outline.length : 0;
          return (
            <button
              key={guide.id}
              data-testid={`playbook-card-${guide.id}`}
              onClick={() => open(guide)}
              className="gos-card gos-card-hover group flex flex-col p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent"
            >
              <div className="flex items-start justify-between gap-3">
                <GlossTile color={guide.color} icon={CardIcon} size={40} iconSize={19} />
                {isRecommended(guide) ? (
                  <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 px-2.5 text-[10px] font-bold text-[#16A34A]" data-testid={`playbook-recommended-${guide.id}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" aria-hidden="true" />
                    Recommended
                  </span>
                ) : null}
              </div>
              <div className="mt-4 text-[10px] font-extrabold uppercase tracking-[0.12em] text-sm-muted">{guide.category}</div>
              <h3 className="font-display mt-1 text-[17px] font-bold leading-snug tracking-[-0.01em] text-sm-ink [text-wrap:balance]">{guide.title}</h3>
              <p className="mt-1.5 line-clamp-3 flex-1 text-[13px] leading-relaxed text-sm-ink/65">{guide.helps}</p>
              <div className="mt-3.5 flex flex-wrap items-center gap-1.5" data-testid={`playbook-tools-${guide.id}`}>
                {shownTags.map((tag) => (
                  <span
                    key={`${tag.kind}-${tag.label}`}
                    data-testid={tag.kind === "outcome" ? `playbook-outcome-${guide.id}-${tag.label}` : undefined}
                    className={tag.kind === "outcome"
                      ? "rounded-full border border-[#22C55E]/25 bg-[#22C55E]/[0.08] px-2 py-0.5 text-[10px] font-semibold text-[#16A34A]"
                      : "rounded-full border border-sm-ink/10 bg-sm-ink/[0.03] px-2 py-0.5 text-[10px] font-semibold text-sm-ink/65"}
                  >
                    {tag.label}
                  </span>
                ))}
                {tags.length > shownTags.length ? <span className="text-[10px] font-semibold text-sm-ink/45">{`+${tags.length - shownTags.length}`}</span> : null}
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-sm-ink/[0.07] pt-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-sm-ink/60">
                  <ShieldCheck size={12} className="shrink-0 text-[#16A34A]" aria-hidden="true" />
                  {steps ? `${steps} steps` : "Guided request"}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-sm-primary">
                  Explore task
                  <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {visibleGuides.length === 0 ? (
        <div className="rounded-xl border border-dashed border-sm-ink/15 px-5 py-10 text-center" data-testid="playbooks-empty">
          <p className="text-sm text-sm-ink/55">No guides match these filters.</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-sm-ink/12 px-3.5 py-1.5 text-xs font-semibold text-sm-ink/65 transition-colors hover:border-sm-accent/45 hover:text-sm-primary"
          >
            <X size={12} /> Clear filters
          </button>
        </div>
      ) : null}

      <GuideDialog
        key={active?.id || "closed"}
        open={Boolean(active)}
        onOpenChange={(openState) => !openState && close()}
        guide={active}
        icon={Icon ?? Lightbulb}
        agentName={agentName}
        answers={answers}
        onAnswer={(key, value) => setAnswers((current) => ({ ...current, [key]: value }))}
        notes={requestNotes}
        onNotes={setRequestNotes}
        sources={sources}
        sourcesStatus={sourcesStatus}
        prompt={promptPreview}
        onCopy={copyPrompt}
        onTry={tryWithAgent}
        launching={launching}
        launchDisabled={launchDisabled}
        launchError={promptPreview.length > 8000 ? "Your prompt is over 8,000 characters. Shorten your context to open it in Chat." : launchError}
        availabilityMessage={controller?.error && availabilityMessage ? controller.error : availabilityMessage}
        onReconnect={controller && !controller.loading && availabilityMessage ? controller.refresh : undefined}
      />
    </div>
  );
}
